import { useEffect, useState, useCallback } from "react";
import { Plus, X, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { useLang } from "@/context/LanguageContext";
import { loc } from "@/lib/localize";

export default function DocumentRequirementsPanel() {
  const { content } = useContent();
  const { lang } = useLang();
  const services = content?.services || [];

  const [requirements, setRequirements] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/document-requirements");
      setRequirements(data.requirements || {});
    } catch {
      toast.error("Failed to load document requirements");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function addSlot(serviceId) {
    setRequirements((r) => ({ ...r, [serviceId]: [...(r[serviceId] || []), { label: "", required: true }] }));
  }

  function updateSlot(serviceId, idx, field, value) {
    setRequirements((r) => {
      const list = [...(r[serviceId] || [])];
      list[idx] = { ...list[idx], [field]: value };
      return { ...r, [serviceId]: list };
    });
  }

  function removeSlot(serviceId, idx) {
    setRequirements((r) => ({ ...r, [serviceId]: (r[serviceId] || []).filter((_, i) => i !== idx) }));
  }

  async function save() {
    setSaving(true);
    try {
      const cleaned = {};
      for (const [serviceId, list] of Object.entries(requirements)) {
        const filtered = list.filter((s) => s.label.trim());
        if (filtered.length) cleaned[serviceId] = filtered;
      }
      const { data } = await api.put("/admin/document-requirements", { requirements: cleaned });
      setRequirements(data.requirements || {});
      toast.success("Document requirements saved");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="text-center py-12"><Loader2 size={24} className="text-[var(--gold)] animate-spin mx-auto" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-requirements-panel">
      <p className="text-sm text-white/55">
        Define labelled upload slots per service on the public Documents page — clients selecting that
        service see exactly these fields instead of a generic file picker. A service with no slots below
        keeps the generic "upload any files" area.
      </p>
      {services.length === 0 && <p className="text-white/40 text-sm">No services found in site content.</p>}
      {services.map((svc) => {
        const slots = requirements[svc.id] || [];
        return (
          <div key={svc.id} className="card-dark p-5">
            <h3 className="font-semibold text-white mb-3">{loc(svc.title, lang)}</h3>
            <div className="space-y-2">
              {slots.map((slot, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    value={slot.label}
                    onChange={(e) => updateSlot(svc.id, idx, "label", e.target.value)}
                    placeholder="Document label, e.g. Passport"
                    className="input-dark flex-1"
                    data-testid={`requirement-label-${svc.id}-${idx}`}
                  />
                  <label className="flex items-center gap-1.5 text-xs text-white/60 shrink-0">
                    <input
                      type="checkbox"
                      checked={slot.required}
                      onChange={(e) => updateSlot(svc.id, idx, "required", e.target.checked)}
                    />
                    Required
                  </label>
                  <button
                    type="button"
                    onClick={() => removeSlot(svc.id, idx)}
                    className="text-white/40 hover:text-red-400 shrink-0"
                    data-testid={`requirement-remove-${svc.id}-${idx}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              {slots.length === 0 && <p className="text-xs text-white/35">No slots configured — generic upload area is shown.</p>}
            </div>
            <button
              type="button"
              onClick={() => addSlot(svc.id)}
              className="btn-outline-gold text-xs py-1.5 px-3 mt-3"
              data-testid={`requirement-add-${svc.id}`}
            >
              <Plus size={12} /> Add document
            </button>
          </div>
        );
      })}
      <button onClick={save} disabled={saving} className="btn-gold text-sm py-2 px-5" data-testid="requirements-save">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        Save Requirements
      </button>
    </div>
  );
}
